import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, FormControlLabel, Switch,
  InputLabel, Select, MenuItem, IconButton, Alert
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloseIcon from '@mui/icons-material/Close';

// Mock transactions data
const recentTransactions = [
  { id: 1, user: 'Emma Johnson', amount: '$99.00', date: '2023-05-15', method: 'Credit Card', status: 'Completed' },
  { id: 2, user: 'Michael Chen', amount: '$49.00', date: '2023-05-14', method: 'PayPal', status: 'Completed' },
  { id: 3, user: 'Sophia Williams', amount: '$199.00', date: '2023-05-10', method: 'Bank Transfer', status: 'Pending' },
  { id: 4, user: 'James Brown', amount: '$99.00', date: '2023-05-15', method: 'Credit Card', status: 'Completed' },
  { id: 5, user: 'Maria Garcia', amount: '$49.00', date: '2023-05-13', method: 'PayPal', status: 'Failed' },
];

// Payment gateway options with their latest integration details
const paymentGateways = [
  { 
    id: 'stripe',
    name: 'Stripe', 
    connected: true, 
    icon: <CreditCardIcon />, 
    description: 'Credit card payments via Stripe',
    apiVersion: 'v2023-10-16',
    webhookVersion: '2023-10-16',
    docsUrl: 'https://stripe.com/docs/api',
    supportedCountries: 44,
    fields: [
      { name: 'publishableKey', label: 'Publishable Key', type: 'text', required: true },
      { name: 'secretKey', label: 'Secret Key', type: 'password', required: true },
      { name: 'webhookSecret', label: 'Webhook Secret', type: 'password', required: false },
      { name: 'testMode', label: 'Test Mode', type: 'boolean', required: false },
    ]
  },
  { 
    id: 'paypal',
    name: 'PayPal', 
    connected: true, 
    icon: <AccountBalanceIcon />, 
    description: 'PayPal checkout and subscriptions',
    apiVersion: 'v2',
    webhookVersion: '1.0',
    docsUrl: 'https://developer.paypal.com/docs/api/overview/',
    supportedCountries: 200,
    fields: [
      { name: 'clientId', label: 'Client ID', type: 'text', required: true },
      { name: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { name: 'sandboxMode', label: 'Sandbox Mode', type: 'boolean', required: false },
    ]
  },
  { 
    id: 'square',
    name: 'Square', 
    connected: false, 
    icon: <AttachMoneyIcon />, 
    description: 'Point of sale and online payments',
    apiVersion: '2023-12-13',
    webhookVersion: '2023-12-13',
    docsUrl: 'https://developer.squareup.com/reference/square',
    supportedCountries: 17,
    fields: [
      { name: 'applicationId', label: 'Application ID', type: 'text', required: true },
      { name: 'accessToken', label: 'Access Token', type: 'password', required: true },
      { name: 'locationId', label: 'Location ID', type: 'text', required: true },
      { name: 'sandboxMode', label: 'Sandbox Mode', type: 'boolean', required: false },
    ]
  },
  { 
    id: 'klarna',
    name: 'Klarna', 
    connected: false, 
    icon: <PaymentIcon />, 
    description: 'Buy now, pay later solution',
    apiVersion: 'v1',
    webhookVersion: '1.0',
    docsUrl: 'https://developers.klarna.com/api/',
    supportedCountries: 20,
    fields: [
      { name: 'username', label: 'API Username', type: 'text', required: true },
      { name: 'password', label: 'API Password', type: 'password', required: true },
      { name: 'region', label: 'Region', type: 'select', required: true, options: [
        { value: 'eu', label: 'Europe' },
        { value: 'us', label: 'United States' },
        { value: 'oc', label: 'Oceania' },
      ]},
      { name: 'testMode', label: 'Test Mode', type: 'boolean', required: false },
    ]
  },
  { 
    id: 'swish',
    name: 'Swish', 
    connected: false, 
    icon: <PhoneIphoneIcon />, 
    description: 'Mobile payment system (Sweden)',
    apiVersion: 'v2.0',
    webhookVersion: 'v1',
    docsUrl: 'https://developer.swish.nu/',
    supportedCountries: 1,
    fields: [
      { name: 'merchantId', label: 'Merchant ID', type: 'text', required: true },
      { name: 'certificateFile', label: 'Certificate File', type: 'file', required: true },
      { name: 'certificatePassword', label: 'Certificate Password', type: 'password', required: true },
      { name: 'testMode', label: 'Test Mode', type: 'boolean', required: false },
    ]
  }
];

function Payments() {
  const [tabValue, setTabValue] = useState(0);
  const [openSettings, setOpenSettings] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenSettings = (gateway) => {
    setSelectedGateway(gateway);
    // Initialize form values with existing settings or defaults
    const initialValues = {};
    gateway.fields.forEach(field => {
      // In a real app, you'd fetch the saved values from your API
      initialValues[field.name] = field.type === 'boolean' ? false : '';
    });
    setFormValues(initialValues);
    setOpenSettings(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
    setSelectedGateway(null);
    setFormValues({});
  };

  const handleInputChange = (event) => {
    const { name, value, checked } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: event.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    if (files && files[0]) {
      setFormValues(prev => ({
        ...prev,
        [name]: files[0].name // In a real app, you'd handle the file upload
      }));
    }
  };

  const handleSaveSettings = () => {
    // Validate required fields
    const requiredFields = selectedGateway.fields.filter(field => field.required);
    const missingFields = requiredFields.filter(field => !formValues[field.name]);
    
    if (missingFields.length > 0) {
      setSaveError(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    // In a real app, you'd make an API call to save the settings
    setTimeout(() => {
      // Simulate API success
      const updatedGateways = paymentGateways.map(g => 
        g.id === selectedGateway.id ? { ...g, connected: true } : g
      );
      
      // In a real app you'd update your state with these gateways
      console.log('Updated gateways:', updatedGateways);
      
      setSaveSuccess(true);
      // Close dialog after showing success message
      setTimeout(() => {
        handleCloseSettings();
      }, 1500);
    }, 1000);
  };

  const renderFieldInput = (field) => {
    switch (field.type) {
      case 'password':
        return (
          <TextField
            key={field.name}
            label={field.label}
            name={field.name}
            type="password"
            value={formValues[field.name] || ''}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required={field.required}
          />
        );
      
      case 'boolean':
        return (
          <FormControlLabel
            key={field.name}
            control={
              <Switch
                name={field.name}
                checked={!!formValues[field.name]}
                onChange={handleInputChange}
                color="primary"
              />
            }
            label={field.label}
            sx={{ my: 1 }}
          />
        );
        
      case 'select':
        return (
          <FormControl fullWidth margin="normal" key={field.name}>
            <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.name}-label`}
              name={field.name}
              value={formValues[field.name] || ''}
              onChange={handleInputChange}
              required={field.required}
            >
              {field.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        
      case 'file':
        return (
          <Box key={field.name} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" gutterBottom>{field.label}{field.required && ' *'}</Typography>
            <input
              type="file"
              name={field.name}
              onChange={handleFileChange}
              style={{ width: '100%' }}
            />
            {formValues[field.name] && (
              <Typography variant="caption" color="primary">
                Selected file: {formValues[field.name]}
              </Typography>
            )}
          </Box>
        );
        
      default: // text
        return (
          <TextField
            key={field.name}
            label={field.label}
            name={field.name}
            value={formValues[field.name] || ''}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            required={field.required}
          />
        );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Payment Integration
      </Typography>

      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab label="Overview" />
        <Tab label="Transactions" />
        <Tab label="Payment Gateways" />
        <Tab label="Settings" />
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h3">$12,845</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +12.5% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Subscriptions
                </Typography>
                <Typography variant="h3">156</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +8.2% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Avg. Subscription Value
                </Typography>
                <Typography variant="h3">$82.30</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  +3.7% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Churn Rate
                </Typography>
                <Typography variant="h3">2.1%</Typography>
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  -0.5% from last month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
              Recent Transactions
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTransactions.slice(0, 5).map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{transaction.user}</TableCell>
                      <TableCell>{transaction.amount}</TableCell>
                      <TableCell>{transaction.date}</TableCell>
                      <TableCell>{transaction.method}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 
                              transaction.status === 'Completed' ? 'success.main' :
                              transaction.status === 'Pending' ? 'warning.main' : 'error.main',
                            color: 'white'
                          }}
                        >
                          {transaction.status}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTransactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{transaction.id}</TableCell>
                  <TableCell>{transaction.user}</TableCell>
                  <TableCell>{transaction.amount}</TableCell>
                  <TableCell>{transaction.date}</TableCell>
                  <TableCell>{transaction.method}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 
                          transaction.status === 'Completed' ? 'success.main' :
                          transaction.status === 'Pending' ? 'warning.main' : 'error.main',
                        color: 'white'
                      }}
                    >
                      {transaction.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined">View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          {paymentGateways.map((gateway) => (
            <Grid item xs={12} md={4} key={gateway.name}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box 
                      sx={{ 
                        mr: 2, 
                        display: 'flex', 
                        p: 1, 
                        borderRadius: 1, 
                        bgcolor: 'action.hover' 
                      }}
                    >
                      {gateway.icon}
                    </Box>
                    <Typography variant="h6">{gateway.name}</Typography>
                  </Box>
                  <Box 
                    sx={{ 
                      display: 'inline-block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: gateway.connected ? 'success.main' : 'error.main',
                      color: 'white',
                      mb: 2
                    }}
                  >
                    {gateway.connected ? 'Connected' : 'Not Connected'}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Button 
                    variant={gateway.connected ? "outlined" : "contained"} 
                    color={gateway.connected ? "primary" : "success"}
                    fullWidth
                    onClick={() => handleOpenSettings(gateway)}
                  >
                    {gateway.connected ? 'Settings' : 'Connect'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">General Settings</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                This is a placeholder for payment settings. In a real application, this would include options for:
              </Typography>
              <ul>
                <li>Default payment gateway</li>
                <li>Currency settings</li>
                <li>Invoice template configuration</li>
                <li>Receipt email templates</li>
                <li>Refund policies</li>
              </ul>
              <Button variant="contained" sx={{ mt: 2 }}>
                Save Settings
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CreditCardIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Payment Methods</Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="body1" sx={{ mb: 2 }}>
                Configure accepted payment methods:
              </Typography>
              <ul>
                <li>Credit/Debit Cards</li>
                <li>PayPal</li>
                <li>Bank Transfers</li>
                <li>Crypto payments</li>
              </ul>
              <Button variant="contained" sx={{ mt: 2 }}>
                Update Methods
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Payment Gateway Settings Dialog */}
      <Dialog open={openSettings} onClose={handleCloseSettings} maxWidth="md" fullWidth>
        {selectedGateway && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {selectedGateway.connected ? 'Configure' : 'Connect'} {selectedGateway.name}
                </Typography>
                <IconButton onClick={handleCloseSettings} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {saveSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {selectedGateway.name} has been successfully {selectedGateway.connected ? 'updated' : 'connected'}!
                </Alert>
              )}
              
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {saveError}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {selectedGateway.description}
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>API Version:</strong> {selectedGateway.apiVersion}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Webhook Version:</strong> {selectedGateway.webhookVersion}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <strong>Supported Countries:</strong> {selectedGateway.supportedCountries}
                    </Typography>
                    <Typography variant="body2">
                      <Button 
                        href={selectedGateway.docsUrl} 
                        target="_blank" 
                        size="small" 
                        sx={{ mt: 1, p: 0 }}
                      >
                        View Documentation
                      </Button>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="h6" gutterBottom>
                Integration Settings
              </Typography>
              
              {selectedGateway.fields.map(field => renderFieldInput(field))}
            </DialogContent>
            
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleCloseSettings}>Cancel</Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSaveSettings}
              >
                {selectedGateway.connected ? 'Save Changes' : 'Connect'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default Payments;
